<template>
  <div class="thread-container">
    <v-btn
      prepend-icon="mdi-arrow-left"
      variant="text"
      class="mb-4"
      @click="$emit('back')"
    >
      Back to Feed
    </v-btn>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-4 text-grey">Peering into the thread...</p>
    </div>

    <!-- Not Found State -->
    <div v-else-if="!thread" class="text-center py-10 px-6">
      <v-icon icon="mdi-help-circle-outline" size="64" class="mb-4 opacity-20"></v-icon>
      <div class="text-h6 font-weight-black mb-2">Record Not Found</div>
      <p class="text-grey mb-6">
        We couldn't find this post. It may have been deleted, or it might be a different type of record (like a note or a repost).
      </p>
      <v-btn
        color="primary"
        variant="flat"
        class="rounded-pill"
        @click="$emit('back')"
      >
        Back to Feed
      </v-btn>
    </div>

    <div v-else-if="thread">
      <!-- Parent Chain Loader -->
      <div v-if="thread.parent && thread.parent.post" class="text-center mb-4">
        <v-btn
          variant="text"
          size="small"
          prepend-icon="mdi-arrow-up-circle-outline"
          class="text-none text-grey-darken-1 load-chain-btn"
          @click="loadParentChain"
        >
          Load previous chain
        </v-btn>
        <div class="chain-line-parent"></div>
      </div>

      <!-- Main Post & Grouped Chain -->
      <PostItem
        :post="thread.post"
        :video-processing="!videoReady && !!getVideoPlaylist(thread.post)"
        @reply="replyingToUri = thread.post.uri"
        @view-thread="$emit('view-thread', $event)"
        @delete="handleDelete($event, true)"
        @view-profile="$emit('view-profile', $event)"
        @quote="$emit('quote', $event)"
        @tag-click="$emit('tag-click', $event)"
      >
        <!-- Grouped Chain: Only the main author's consecutive replies -->
        <template v-if="authorChain.length > 0 && threadRoot">
          <ThreadReplyItem
            :reply="authorChain[0]"
            grouped
            is-author-chain
            :author-handle="thread.post.author.handle"
            :root="threadRoot"
            :replying-to-uri="replyingToUri"
            @reply="replyingToUri = $event.uri"
            @view-thread="$emit('view-thread', $event)"
            @delete="handleDelete($event)"
            @view-profile="$emit('view-profile', $event)"
            @quote="$emit('quote', $event)"
            @tag-click="$emit('tag-click', $event)"
            @reply-success="onReplySuccess"
            @reply-cancel="replyingToUri = ''"
          />
        </template>
      </PostItem>
      
      <div v-if="replyingToUri === thread.post.uri && threadRoot" class="reply-input-wrapper mb-2">
        <ReplyInput 
          :root="threadRoot"
          :parent="{ uri: thread.post.uri, cid: thread.post.cid }"
          @success="onReplySuccess"
          @cancel="replyingToUri = ''"
        />
      </div>

      <!-- Interaction Stats -->
      <InteractionStats 
        v-if="thread.post"
        :repost-count="thread.post.repostCount || 0"
        :quote-count="thread.post.quoteCount || 0"
        :like-count="thread.post.likeCount || 0"
        @show-reposts="showUserList('reposts')"
        @show-quotes="showUserList('quotes')"
        @show-likes="showUserList('likes')"
      />

      <!-- Replies -->
      <div class="replies-section mt-8 mb-6">
        <h3 class="discussion-header text-h6 font-weight-black d-flex align-center text-left">
          <v-icon icon="mdi-forum-outline" class="mr-2" color="secondary"></v-icon>
          Community Discussion
        </h3>
        
        <template v-if="communityReplies.length > 0 && threadRoot">
          <ThreadReplyItem
            v-for="reply in communityReplies"
            :key="reply.post.uri"
            :reply="reply"
            :grouped="false"
            :is-author-chain="false"
            :author-handle="reply.post.author.handle"
            :root="threadRoot"
            :replying-to-uri="replyingToUri"
            @reply="replyingToUri = $event.uri"
            @view-thread="$emit('view-thread', $event)"
            @delete="handleDelete($event)"
            @view-profile="$emit('view-profile', $event)"
            @quote="$emit('quote', $event)"
            @tag-click="$emit('tag-click', $event)"
            @reply-success="onReplySuccess"
            @reply-cancel="replyingToUri = ''"
          />
        </template>
        <v-alert v-else type="info" variant="tonal" class="discussion-empty-alert rounded-xl">
          No replies yet. Be the first to join the conversation!
        </v-alert>
      </div>
    </div>
    
    <!-- Delete Post Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card class="rounded-xl border border-white-05 bg-surface">
        <v-card-title class="text-h6 font-weight-black pa-6 pb-2">
          Delete Post?
        </v-card-title>
        <v-card-text class="pa-6 pt-0 text-grey-lighten-1">
          Are you sure you want to delete this post? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            class="font-weight-bold"
            @click="showDeleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            class="rounded-pill font-weight-bold px-6"
            @click="confirmDelete"
            :loading="isDeleting"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- User List Dialog -->
    <UserListDialog
      v-model="userListVisible"
      :type="userListType"
      :post-uri="thread?.post?.uri || postUri"
    />

    <QuotesDialog
      v-model="quotesVisible"
      :post-uri="thread?.post?.uri || postUri"
      @view-thread="$emit('view-thread', $event)"
      @view-profile="$emit('view-profile', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import { getPostThread, deletePost } from '@/services/bluesky';
import PostItem from '@/components/PostItem.vue';
import ReplyInput from '@/components/ReplyInput.vue';
import ThreadReplyItem from '@/components/ThreadReplyItem.vue';
import InteractionStats from '@/components/InteractionStats.vue';
import UserListDialog from '@/components/UserListDialog.vue';
import QuotesDialog from '@/components/QuotesDialog.vue';

const props = defineProps<{
  handle: string;
  rkey: string;
}>();

const postUri = computed(() => {
  const h = props.handle?.startsWith('@') ? props.handle.slice(1) : props.handle;
  return `at://${h}/app.bsky.feed.post/${props.rkey}`;
});

const emit = defineEmits(['back', 'view-profile', 'view-thread', 'quote', 'tag-click']);

const thread = ref<any>(null);
const loading = ref(true);
const replyingToUri = ref('');
const currentUri = ref(postUri.value);
const videoReady = ref(false);
let videoPollTimer: ReturnType<typeof setTimeout> | null = null;

const getVideoPlaylist = (post: any): string | null => {
  const embed = post?.embed;
  return embed?.playlist || embed?.media?.playlist || null;
};

const pollVideoReady = (playlist: string) => {
  if (videoPollTimer) clearTimeout(videoPollTimer);
  videoReady.value = false;
  const deadline = Date.now() + 60_000; // stop after 60s

  const check = async () => {
    if (Date.now() > deadline) return;
    try {
      const res = await fetch(playlist);
      if (res.ok) {
        videoReady.value = true;
        loadThread(true); // silent reload — PostItem will reinitialize HLS
        return;
      }
    } catch { /* ignore */ }
    videoPollTimer = setTimeout(check, 2000);
  };
  check();
};

// User List Dialog state
const userListVisible = ref(false);
const userListType = ref<'likes' | 'reposts' | 'quotes'>('likes');
const quotesVisible = ref(false);

const showUserList = (type: 'likes' | 'reposts' | 'quotes') => {
  if (type === 'quotes') {
    quotesVisible.value = true;
  } else {
    userListType.value = type;
    userListVisible.value = true;
  }
};

const loadThread = async (silent = false) => {
  if (!silent) loading.value = true;
  try {
    thread.value = await getPostThread(postUri.value);
    if (!thread.value) {
      console.warn('Thread returned null for URI:', postUri.value);
    } else {
      // If the root post has a video not yet processed by video.bsky.app, poll until ready
      const playlist = getVideoPlaylist(thread.value.post);
      if (playlist && !videoReady.value) {
        pollVideoReady(playlist);
      }
    }
  } catch (err: any) {
    console.error('Failed to load thread:', err);
  } finally {
    if (!silent) loading.value = false;
  }
};

onBeforeUnmount(() => {
  if (videoPollTimer) clearTimeout(videoPollTimer);
});

const loadParentChain = () => {
  if (thread.value?.parent?.post) {
    emit('view-thread', thread.value.parent.post);
  }
};

const onReplySuccess = () => {
  replyingToUri.value = '';
  loadThread(); // Refresh thread to show new reply
};

const showDeleteDialog = ref(false);
const postToDelete = ref<any>(null);
const isDeletingRoot = ref(false);
const isDeleting = ref(false);

const handleDelete = (post: any, isRoot = false) => {
  postToDelete.value = post;
  isDeletingRoot.value = isRoot;
  showDeleteDialog.value = true;
};

const confirmDelete = async () => {
  if (!postToDelete.value) return;
  isDeleting.value = true;
  try {
    await deletePost(postToDelete.value.uri);
    showDeleteDialog.value = false;
    if (isDeletingRoot.value) {
      emit('back');
    } else {
      loadThread();
    }
  } catch (err: any) {
    window.alert('Failed to delete post: ' + (err.message || 'Unknown error'));
  } finally {
    isDeleting.value = false;
    postToDelete.value = null;
  }
};

onMounted(loadThread);

watch(() => postUri.value, () => {
  if (videoPollTimer) clearTimeout(videoPollTimer);
  videoReady.value = false;
  loadThread();
});

// Logic to extract the correct thread root
const threadRoot = computed(() => {
  if (!thread.value?.post) return null;
  
  // If the post record has a reply property, the root is in there
  const record = thread.value.post.record || thread.value.post;
  if (record.reply?.root) {
    return {
      uri: record.reply.root.uri,
      cid: record.reply.root.cid
    };
  }
  
  // Otherwise, the current post is the root
  return {
    uri: thread.value.post.uri,
    cid: thread.value.post.cid
  };
});

// Logic to separate the author's primary thread from community replies
const authorChain = computed(() => {
  if (!thread.value || !thread.value.replies || thread.value.replies.length === 0) return [];
  
  const rootAuthor = thread.value.post.author.handle;
  const chain: any[] = [];
  
  // Look at the first top-level reply
  let current = thread.value.replies[0];
  
  // If the first reply is from the root author, it starts our chain
  if (current.post.author.handle === rootAuthor) {
    chain.push(current);
  }
  
  return chain;
});

const communityReplies = computed(() => {
  if (!thread.value || !thread.value.replies) return [];
  
  const rootAuthor = thread.value.post.author.handle;
  const community: any[] = [];
  
  // Recursively find all entry points for community discussion
  // An entry point is any reply that isn't part of the primary author chain
  const findCommunityRoots = (item: any, inAuthorChain: boolean) => {
    if (!item.replies) return;
    
    item.replies.forEach((r: any, idx: number) => {
      // It's part of the official chain ONLY if it's the first child AND from the original author
      const isStillAuthorChain = inAuthorChain && Number(idx) === 0 && r.post.author.handle === rootAuthor;
      
      if (isStillAuthorChain) {
        findCommunityRoots(r, true);
      } else {
        // This is a community reply, a branch, or a break in the author chain.
        // It becomes a new block in the community section.
        community.push(r);
      }
    });
  };
  
  findCommunityRoots({ replies: thread.value.replies }, true);
  
  // Sort community replies by time (ascending)
  return community.sort((a, b) => new Date(a.post.indexedAt).getTime() - new Date(b.post.indexedAt).getTime());
});
</script>

<style scoped>
.thread-container {
  width: 100%;
  animation: slideIn 0.4s ease-out;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.nested-replies {
  margin-left: 20px;
  border-left: 2px solid rgba(255, 255, 255, 0.05);
  padding-left: 10px;
}

.reply-wrapper {
  position: relative;
}

.reply-input-wrapper {
  animation: expand 0.3s ease-out;
}

@keyframes expand {
  from { opacity: 0; max-height: 0; overflow: hidden; }
  to { opacity: 1; max-height: 500px; }
}

.load-chain-btn {
  opacity: 0.6;
  transition: all 0.2s;
}

.load-chain-btn:hover {
  opacity: 1;
  color: var(--v-theme-primary) !important;
}

.chain-line-parent {
  width: 2px;
  height: 20px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.1));
  margin: 0 auto;
}

.discussion-header {
  margin-left: 0;
  margin-top: 24px !important;
  margin-bottom: 24px !important;
}

.discussion-empty-alert {
  margin-left: 0;
}

@media (max-width: 600px) {
  .discussion-header,
  .discussion-empty-alert {
    margin-left: 0;
  }
}
</style>
